import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import { catchError, map, of, switchMap, takeUntil, tap } from 'rxjs';
import { AuthenticationService } from '../../services/authentication.service';
import { AuthApiActions, AuthActions, LoginPageActions, ChangePasswordPageActions } from './auth.actions';
import { UserRole } from '../../services/role.service';
import { mapToUserRoles } from '../../utils/jwt-roles.util';
import { TwoFactorService } from '../../services/two-factor.service';
import { TwoFactorSetupActions, TwoFactorSetupApiActions } from './auth.actions';
import { NotificationService } from '../../services/notification.service';

@Injectable()
export class AuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly router = inject(Router);
  private readonly twoFactorService = inject(TwoFactorService);
  private readonly notificationService = inject(NotificationService);

  readonly login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginPageActions.loginRequested),
      switchMap(({ email, password }) => this.authenticationService.login(email, password).pipe(
        switchMap((response) => {
          if ('mustChangePassword' in response) {
            return of(AuthApiActions.mustChangePasswordRequired({ email }));
          }

          if ('requiresTwoFactor' in response) {
            return of(AuthApiActions.twoFactorChallengeRequired({
              challengeId: response.challengeId,
              expiresAt: response.expiresAt,
              email,
            }));
          }

          localStorage.setItem('accessToken', response.accessToken);
          return of(this.loginSucceeded(response.accessToken, response.roles ?? [], response.isTwoFactorEnabled ?? false, email));
        }),
        catchError((error: { message?: string }) => of(
          AuthApiActions.loginFailed({ error: error?.message ?? 'Login failed' }),
        )),
      )),
    ),
  );

  readonly refresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshRequested),
      switchMap(() => this.authenticationService.refreshToken(localStorage.getItem('accessToken')).pipe(
        takeUntil(this.actions$.pipe(ofType(AuthActions.logoutRequested))),
        tap(({ accessToken, refreshToken }) => {
          localStorage.setItem('accessToken', accessToken);
          if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
        }),
        map(({ accessToken, refreshToken }) => AuthApiActions.refreshSucceeded({ token: accessToken, refreshToken })),
        catchError((error: { message?: string }) => of(
          AuthApiActions.refreshFailed({ error: error?.message ?? 'Token refresh failed' }),
        )),
      )),
    ),
  );

  readonly verifyTwoFactor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.twoFactorVerificationRequested),
      switchMap(({ challengeId, otpOrRecoveryCode, email }) => this.authenticationService.completeTwoFactorChallenge(challengeId, otpOrRecoveryCode).pipe(
        switchMap((response) => {
          localStorage.setItem('accessToken', response.accessToken);
          return of(
            this.loginSucceeded(response.accessToken, response.roles ?? [], response.isTwoFactorEnabled ?? false, email),
            AuthApiActions.twoFactorVerificationSucceeded(),
          );
        }),
        catchError((error: { message?: string }) => of(
          AuthApiActions.twoFactorVerificationFailed({ error: error?.message ?? 'Two-factor verification failed' }),
        )),
      )),
    ),
  );

  readonly navigateAfterLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthApiActions.loginSucceeded),
      tap(({ roles, isTwoFactorEnabled }) => {
        const landingRoute = this.getLandingRoute(roles);
        void this.router.navigate([landingRoute]);
      }),
    ),
    { dispatch: false },
  );

  readonly navigateToTwoFactorChallenge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthApiActions.twoFactorChallengeRequired),
      tap(() => void this.router.navigate(['/auth/2fa'])),
    ),
    { dispatch: false },
  );

  readonly navigateToChangePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthApiActions.mustChangePasswordRequired),
      tap(() => void this.router.navigate(['/auth/change-password'])),
    ),
    { dispatch: false },
  );

  readonly changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangePasswordPageActions.changePasswordRequested),
      switchMap(({ email, currentPassword, newPassword }) => this.authenticationService
        .changePassword(email, currentPassword, newPassword)
        .pipe(
          map(() => AuthApiActions.changePasswordSucceeded()),
          catchError((error: { message?: string }) => of(
            AuthApiActions.changePasswordFailed({ error: error?.message ?? 'Unable to change password' }),
          )),
        )),
    ),
  );

  readonly navigateAfterChangePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthApiActions.changePasswordSucceeded),
      tap(() => {
        this.notificationService.success('AUTH.CHANGE_PASSWORD_SUCCESS');
        void this.router.navigate(['/auth/login']);
      }),
    ),
    { dispatch: false },
  );

  readonly rehydrate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.appInitializationRequested),
      map(() => localStorage.getItem('accessToken')),
      switchMap((token) => {
        if (!token || token.split('.').length !== 3) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          return of(AuthActions.appRehydrationFailed({ error: 'No valid session found' }));
        }
        try {
          const payload = JSON.parse(atob(token.split('.')[1])) as Record<string, unknown>;
          const roles = mapToUserRoles(payload['Role']);
          const isTwoFactorEnabled = this.booleanClaim(payload['IsTwoFactorEnabled'])
            || this.booleanClaim(payload['isTwoFactorEnabled']);
          return of(AuthActions.appRehydrated({
            token,
            roles,
            user: { id: String(payload['UserId'] ?? ''), email: null },
            isTwoFactorEnabled,
            twoFactorVerified: false,
          }));
        } catch {
          return of(AuthActions.appRehydrationFailed({ error: 'Unable to restore session' }));
        }
      }),
    ),
  );

  readonly logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logoutRequested),
      tap(() => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userRoles');
      }),
      map(() => AuthApiActions.logoutCompleted()),
    ),
  );

  readonly navigateAfterLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthApiActions.logoutCompleted),
      tap(() => void this.router.navigate(['/auth/login'])),
    ),
    { dispatch: false },
  );

  readonly refreshFailureLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthApiActions.refreshFailed),
      map(() => AuthActions.logoutRequested()),
    ),
  );

  readonly enableTwoFactor$ = createEffect(() => this.actions$.pipe(
    ofType(TwoFactorSetupActions.enableRequested),
    switchMap(({ currentOtp }) => this.twoFactorService.enroll(currentOtp).pipe(
      map((response) => TwoFactorSetupApiActions.enableSucceeded({ response })),
      catchError((error: { message?: string }) => of(TwoFactorSetupApiActions.enableFailed({ error: error?.message ?? 'Unable to enable two-factor authentication' }))),
    )),
  ));

  readonly confirmTwoFactor$ = createEffect(() => this.actions$.pipe(
    ofType(TwoFactorSetupActions.confirmRequested),
    switchMap(({ otp }) => this.twoFactorService.confirmEnrollment(otp).pipe(
      map(({ recoveryCodes }) => TwoFactorSetupApiActions.confirmSucceeded({ recoveryCodes })),
      catchError((error: { message?: string }) => of(TwoFactorSetupApiActions.confirmFailed({ error: error?.message ?? 'Unable to confirm two-factor setup' }))),
    )),
  ));

  readonly disableTwoFactor$ = createEffect(() => this.actions$.pipe(
    ofType(TwoFactorSetupActions.disableRequested),
    switchMap(({ password, otpOrRecoveryCode }) => this.twoFactorService.disable(password, otpOrRecoveryCode).pipe(
      map(() => TwoFactorSetupApiActions.disableSucceeded()),
      catchError((error: { message?: string }) => of(TwoFactorSetupApiActions.disableFailed({ error: error?.message ?? 'Unable to disable two-factor authentication' }))),
    )),
  ));

  readonly regenerateTwoFactorCodes$ = createEffect(() => this.actions$.pipe(
    ofType(TwoFactorSetupActions.regenerateRequested),
    switchMap(({ password, otpOrRecoveryCode }) => this.twoFactorService.regenerateRecoveryCodes(password, otpOrRecoveryCode).pipe(
      map(({ recoveryCodes }) => TwoFactorSetupApiActions.regenerateSucceeded({ recoveryCodes })),
      catchError((error: { message?: string }) => of(TwoFactorSetupApiActions.regenerateFailed({ error: error?.message ?? 'Unable to regenerate recovery codes' }))),
    )),
  ));

  private userIdFromToken(token: string): string {
    try {
      const payload = JSON.parse(atob(token.split('.')[1])) as Record<string, unknown>;
      return String(payload['UserId'] ?? '');
    } catch {
      return '';
    }
  }

  private loginSucceeded(
    token: string,
    roles: readonly UserRole[],
    isTwoFactorEnabled: boolean,
    email: string,
  ) {
    return AuthApiActions.loginSucceeded({
      user: { id: this.userIdFromToken(token), email },
      token,
      roles,
      isTwoFactorEnabled,
    });
  }

  private getLandingRoute(roles: readonly UserRole[]): string {
    if (roles.includes('Administrator') || roles.includes('Developer')) {
      return '/admin/dashboard';
    }

    if (roles.includes('Donator')) {
      return '/admin/my-donations';
    }

    return '/404';
  }

  private booleanClaim(value: unknown): boolean {
    return value === true || value === 'true';
  }
}